import axiosInstance from "../config/axiosInstance";
import { ApiRoutes } from "../constants/ApiRoutes";
import { QueryKeys } from "../constants/QueryKeys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { generatePath } from "react-router-dom";
import { cleanParams } from "../Utilities/queryParamUtils";
import {
  ArchivedDependentResponse,
  ArchiveRecordActionParams,
  GetArchiveRecordsParams,
  GetArchiveTicketsResponse,
} from "../Types/restoreDatabaseTypes";
import { MutateKeys } from "../constants/MutateKeys";

const RestoreDatabaseService = () => {
  const queryClient = useQueryClient();
  const { GET_ARCHIVE_RECORDS, ARCHIVE_RECORD_ACTIONS_ROUTE } = ApiRoutes;
  const { GET_ARCHIVE_RECORDS_KEY } = QueryKeys;
  const { RESTORE_ARCHIVE_RECORD_KEY, DELETE_ARCHIVE_RECORD_KEY } = MutateKeys;

  const GetArchivedRecords = (
    params?: GetArchiveRecordsParams,
    pageSizeChanged: boolean,
    enabledFlag: boolean,
  ) => {
    const { type, ...rest } = params;
    const getArchivedRecords = async (): Promise<
      GetArchiveTicketsResponse | ArchivedDependentResponse
    > => {
      const url = generatePath(GET_ARCHIVE_RECORDS, {
        type: type,
      });
      const cleanedParams = cleanParams({
        ...rest,
        setDefaultPageSize: pageSizeChanged,
      });
      const { data } = await axiosInstance.get<
        GetArchiveTicketsResponse | ArchivedDependentResponse
      >(url, {
        params: cleanedParams,
      });

      return data;
    };
    return useQuery({
      queryKey: [GET_ARCHIVE_RECORDS_KEY, params],
      queryFn: getArchivedRecords,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      enabled: !!params?.type && !!enabledFlag && !!params?.perPage,
    });
  };

  const RestoreArchiveRecord = () => {
    const restoreArchiveRecord = async (params?: ArchiveRecordActionParams) => {
      const { id, ...rest } = params;
      const url = generatePath(ARCHIVE_RECORD_ACTIONS_ROUTE, {
        id: id,
      });
      const cleanedParams = cleanParams(rest);
      const { data } = await axiosInstance.post(
        url,
        {},
        {
          params: cleanedParams,
        },
      );
      return data;
    };
    return useMutation({
      mutationFn: restoreArchiveRecord,
      mutationKey: [RESTORE_ARCHIVE_RECORD_KEY],
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [GET_ARCHIVE_RECORDS_KEY],
        });
      },
    });
  };

  const DeleteArchiveRecord = () => {
    const deleteArchiveRecord = async (params?: ArchiveRecordActionParams) => {
      const { id, ...rest } = params;
      const url = generatePath(ARCHIVE_RECORD_ACTIONS_ROUTE, {
        id: id,
      });
      const cleanedParams = cleanParams(rest);
      const { data } = await axiosInstance.delete(url, {
        params: cleanedParams,
      });
      return data;
    };
    return useMutation({
      mutationFn: deleteArchiveRecord,
      mutationKey: [DELETE_ARCHIVE_RECORD_KEY],
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [GET_ARCHIVE_RECORDS_KEY],
        });
      },
    });
  };

  return {
    GetArchivedRecords,
    RestoreArchiveRecord,
    DeleteArchiveRecord,
  };
};

export default RestoreDatabaseService;
